import React, { useEffect, useState } from "react";
import {useParams} from "react-router-dom"; // To get shareId and submissionId from URL
import FormRenderer from "./form-renderer"; // Adjust path
import {useToast} from "../../components/ui/use-toast"; // Adjust path
import {
  getPublicForm,
  getFormSubmission,
  getFormSubmissionByClientPurchaseOrderId,
} from "../../../ApiCalls/customForms"; // Add getFormSubmission import
import { Loader2 } from "lucide-react";
import mantisLogoLight from "../../../Assets/img/mantisBlackLogo.png";
import { brand } from "../../../../src/config";
import "../../components/ui/skeleton";
import IprFormRenderer from "./ipr-form-renderer";

function PublicForm() {
  const {shareId, submissionId} = useParams(); // Get both shareId and submissionId from URL
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState({});
  const [previousSubmission, setPreviousSubmission] = useState(null);
  const {toast} = useToast();

  useEffect(() => {
    if (shareId) {
      setLoading(true);
      setError(null);
      fetchPublicForm(shareId);
    }
  }, [shareId, toast]);

  // Add new effect to fetch previous submission if submissionId exists
  useEffect(() => {
    if (submissionId && form && !previousSubmission) {
      fetchPreviousSubmission(submissionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, form]);

  // Función para obtener la respuesta del formulario por ID de orden de compra del cliente
  const handleGetFormResponseByPurchaseOrderId = async (purchaseOrderId) => {
    setLoading(true);
    try {
      const response = await getFormSubmissionByClientPurchaseOrderId(
        shareId,
        purchaseOrderId
      );
      console.log("Response for purchase order ID:", response);
      if (response.success) {
        setPreviousSubmission(response.data);
      } else {
        console.warn(
          "Could not fetch submission by purchase order ID:",
          response.error
        );
      }
    } catch (e) {
      console.error("Error fetching submission by purchase order ID:", e);
      toast({
        title: "Error al cargar datos previos",
        description:
          "No se pudieron cargar los datos de la presentación anterior por ID de orden de compra.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousSubmission = async (submissionId) => {
    try {
      const response = await getFormSubmission(submissionId);
      console.log("Previous submission response:", response);
      if (response.success) {
        setPreviousSubmission(response.data);
      } else {
        console.warn("Could not fetch previous submission:", response.error);
      }
    } catch (error) {
      console.error("Error fetching previous submission:", error);
      toast({
        title: "Error al cargar datos previos",
        description:
          "No se pudieron cargar los datos de la presentación anterior.",
        variant: "destructive"
      });
    }
  };

  const fetchPublicForm = async (shareId) => {
    try {
      const response = await getPublicForm(shareId);
      console.log("Public form response:", response);
      if (response.success) {
        setForm(response.form);
        setAdditionalInfo(response.additionalInfo || {});
      } else {
        throw new Error(response.error || "Error fetching form");
      }
    } catch (error) {
      console.error("Failed to fetch public form:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="render-order-fullscreen">
        <header className="render-order-header">
          <img
            src={mantisLogoLight}
            alt={`${brand} Logo`}
            className="mantis-logo"
          />
          <h1 className="mantis-title">Mantis Forms</h1>
        </header>
        <main className="render-order-main">
          <div className="loading-section">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto" />
            <p className="text-lg text-gray-600 font-medium">
              Cargando formulario...
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="render-order-fullscreen">
        <header className="render-order-header">
          <img
            src={mantisLogoLight}
            alt={`${brand} Logo`}
            className="mantis-logo"
          />
          <h1 className="mantis-title">Mantis Forms</h1>
        </header>
        <main className="render-order-main">
          <div className="ipr-status-section">
            <div className="error-message">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                Error al cargar el formulario
              </h3>
              <p className="text-gray-600">{error}</p>
              <p className="text-gray-500">
                Por favor, verifica el enlace o inténtalo más tarde.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="render-order-fullscreen">
        <header className="render-order-header">
          <img
            src={mantisLogoLight}
            alt={`${brand} Logo`}
            className="mantis-logo"
          />
          <h1 className="mantis-title">Mantis Forms</h1>
        </header>
        <main className="render-order-main">
          <div className="ipr-status-section">
            <div className="error-message">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                Formulario no encontrado
              </h3>
              <p className="text-gray-600">
                El formulario que buscas no existe o ya no está disponible.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="render-order-fullscreen">
      <header className="render-order-header">
        <img
          src={mantisLogoLight}
          alt={`${brand} Logo`}
          className="mantis-logo"
        />
        <h1 className="mantis-title">Mantis Forms</h1>
        {submissionId && (
          <div className="text-sm text-gray-600 mt-2">
            Editando presentación anterior
          </div>
        )}
      </header>
      <main className="render-order-main">
        <div className="p-8 md:p-12">
          {form.formType === "IPR" ? (
            <IprFormRenderer
              form={form}
              additionalInfo={additionalInfo}
              isPreview={false}
              shareId={shareId}
              previousSubmission={previousSubmission}
              submissionId={submissionId}
              onGetFormResponseByPurchaseOrderId={
                handleGetFormResponseByPurchaseOrderId
              }
            />
          ) : (
            <FormRenderer
              form={form}
              additionalInfo={additionalInfo}
              isPreview={false}
              shareId={shareId}
              previousSubmission={previousSubmission}
              submissionId={submissionId}
              onGetFormResponseByPurchaseOrderId={
                handleGetFormResponseByPurchaseOrderId
              }
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default PublicForm;
